"use client";

import type { Key } from "@react-types/shared";
import { useEffect, useRef, useState } from "react";

const SELECTED_OPTION_SELECTOR = '[role="option"][aria-selected="true"]';

type UseSelectClearOnReselectParams = {
	isClearable: boolean;
	isDisabled?: boolean;
	value?: Key | null;
	defaultValue?: Key | null;
	isOpen?: boolean;
	onOpenChange?: (isOpen: boolean) => void;
	onChange?: (key: Key | null) => void;
};

function getEventElement(target: EventTarget | null): Element | null {
	if (target instanceof Element) {
		return target;
	}
	if (target instanceof Text) {
		return target.parentElement;
	}
	return null;
}

function normalizeKey(key: Key | null | undefined): Key | null {
	if (key === undefined || key === "") {
		return null;
	}
	return key;
}

function hasSelectedKey(key: Key | null): key is Key {
	return key !== null;
}

function findSelectedOption(element: Element): HTMLElement | null {
	const selectedOption = element.closest(SELECTED_OPTION_SELECTOR);
	if (!(selectedOption instanceof HTMLElement)) {
		return null;
	}
	return selectedOption;
}

function getOptionKey(option: HTMLElement, fallback: Key): string {
	return String(
		option.getAttribute("data-key") ?? option.dataset.key ?? fallback
	);
}

function stopEvent(event: Event): void {
	event.preventDefault();
	event.stopPropagation();
	event.stopImmediatePropagation();
}

function isEventInsideListBox(
	listBox: HTMLDivElement | null,
	target: EventTarget | null
): Element | null {
	const element = getEventElement(target);
	if (!(listBox && element && listBox.contains(element))) {
		return null;
	}
	return element;
}

type TryClearOnReselectParams = {
	listBox: HTMLDivElement | null;
	currentKey: Key;
	hasCleared: boolean;
	onClear: (optionKey: string) => void;
};

function tryClearOnReselect(
	event: Event,
	params: TryClearOnReselectParams
): boolean {
	const element = isEventInsideListBox(params.listBox, event.target);
	const selectedOption = element ? findSelectedOption(element) : null;
	if (!selectedOption) {
		return params.hasCleared;
	}

	stopEvent(event);

	if (params.hasCleared) {
		return true;
	}

	params.onClear(getOptionKey(selectedOption, params.currentKey));
	return true;
}

export function useSelectClearOnReselect({
	isClearable,
	isDisabled,
	value,
	defaultValue,
	isOpen: isOpenProp,
	onOpenChange,
	onChange,
}: UseSelectClearOnReselectParams) {
	const isOpenControlled = isOpenProp !== undefined;
	const isValueControlled = value !== undefined;
	const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
	const [uncontrolledValue, setUncontrolledValue] = useState<Key | null>(
		normalizeKey(defaultValue)
	);
	const [selectInstanceId, setSelectInstanceId] = useState(0);

	const isOpen = isOpenProp ?? uncontrolledOpen;
	const selectedKey = isValueControlled
		? normalizeKey(value)
		: uncontrolledValue;

	const listBoxRef = useRef<HTMLDivElement>(null);
	const onChangeRef = useRef(onChange);
	const onOpenChangeRef = useRef(onOpenChange);
	const suppressedKeyRef = useRef<string | null>(null);
	const selectedKeyRef = useRef(selectedKey);
	const isOpenControlledRef = useRef(isOpenControlled);
	const isValueControlledRef = useRef(isValueControlled);
	const suppressFrameRef = useRef<number | null>(null);

	onChangeRef.current = onChange;
	onOpenChangeRef.current = onOpenChange;
	selectedKeyRef.current = selectedKey;
	isOpenControlledRef.current = isOpenControlled;
	isValueControlledRef.current = isValueControlled;

	useEffect(
		() => () => {
			if (suppressFrameRef.current !== null) {
				window.cancelAnimationFrame(suppressFrameRef.current);
				suppressFrameRef.current = null;
			}
			suppressedKeyRef.current = null;
		},
		[]
	);

	useEffect(() => {
		if (!(isClearable && isOpen) || isDisabled) {
			return;
		}

		const currentKey = selectedKeyRef.current;
		if (!hasSelectedKey(currentKey)) {
			return;
		}

		let hasClearedThisOpen = false;

		const applyClear = (optionKey: string) => {
			suppressedKeyRef.current = optionKey;
			if (suppressFrameRef.current !== null) {
				window.cancelAnimationFrame(suppressFrameRef.current);
			}
			suppressFrameRef.current = window.requestAnimationFrame(() => {
				suppressFrameRef.current = window.requestAnimationFrame(() => {
					if (suppressedKeyRef.current === optionKey) {
						suppressedKeyRef.current = null;
					}
					suppressFrameRef.current = null;
				});
			});

			if (!isValueControlledRef.current) {
				setUncontrolledValue(null);
			}
			onChangeRef.current?.(null);

			if (!isOpenControlledRef.current) {
				setUncontrolledOpen(false);
			}
			onOpenChangeRef.current?.(false);
			setSelectInstanceId((currentId) => currentId + 1);
		};

		const clearIfReselecting = (event: Event) => {
			hasClearedThisOpen = tryClearOnReselect(event, {
				currentKey,
				hasCleared: hasClearedThisOpen,
				listBox: listBoxRef.current,
				onClear: applyClear,
			});
		};

		window.addEventListener("pointerdown", clearIfReselecting, true);
		window.addEventListener("pointerup", clearIfReselecting, true);
		window.addEventListener("click", clearIfReselecting, true);

		return () => {
			window.removeEventListener("pointerdown", clearIfReselecting, true);
			window.removeEventListener("pointerup", clearIfReselecting, true);
			window.removeEventListener("click", clearIfReselecting, true);
		};
	}, [isClearable, isDisabled, isOpen]);

	const handleChange = (key: Key | null) => {
		if (key !== null && key !== undefined) {
			const next = String(key);
			if (suppressedKeyRef.current === next) {
				return;
			}
			suppressedKeyRef.current = null;
			if (suppressFrameRef.current !== null) {
				window.cancelAnimationFrame(suppressFrameRef.current);
				suppressFrameRef.current = null;
			}
		}

		if (!isValueControlled) {
			setUncontrolledValue(normalizeKey(key));
		}
		onChange?.(key);
	};

	const handleOpenChange = (nextOpen: boolean) => {
		if (!isOpenControlled) {
			setUncontrolledOpen(nextOpen);
		}
		onOpenChange?.(nextOpen);
	};

	return {
		listBoxRef,
		isOpen,
		onOpenChange: handleOpenChange,
		onChange: handleChange,
		selectInstanceId,
		value: selectedKey,
	};
}
